<script>
  import { onMount } from "svelte";
  import { DataTableSkeleton } from "carbon-components-svelte";
  import Header from "lib/components/Header.svelte";
  import { SectionWrapper, Select } from "paxray-components-svelte";
  import SearchNew from "lib/components/searchNew.svelte";
  import SoftwareCatalogTable from "./components/SoftwareCatalogTable.svelte";
  import Modal from "lib/components/Modal.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ROUTES } from "@utils/routes.js";

  const headers = [
    { key: "l1", value: "L1 Process" },
    { key: "l2", value: "L2 Subprocess" },
    { key: "l3", value: "L3 Task Group" },
    { key: "l4", value: "L4 Task" },
    { key: "type", value: "Type" },
    { key: "teams", value: "Teams" },
    { key: "cost_pa", value: "Annual Cost" },
    { key: "time_pa", value: "Annual Time" },
    { key: "frequency_pa", value: "Frequency per Year" },
    { key: "avg_time", value: "Avg. Time" },
    { key: "projects", value: "Projects" },
    { key: "actions", value: "Actions" },
  ];
  const numericKeys = new Set([
    "cost_pa",
    "time_pa",
    "frequency_pa",
    "avg_time",
  ]);

  let processes = [];
  let projects = [];
  let summary = {};
  let selectedProject = "";
  let selectedL1 = "";
  let selectedL2 = "";
  let selectedType = "";
  let selectedTeam = "";
  let search = "";
  let page = 1;
  let pageSize = 25;
  let sortKey = "time_pa";
  let sortDirection = "desc";
  let loading = true;
  let error = "";
  let actionsOpen = false;
  let selectedActionRow = null;

  $: l1Options = uniqueValues(processes.map((row) => row.l1));
  $: l2Options = uniqueValues(
    processes
      .filter((row) => !selectedL1 || row.l1 === selectedL1)
      .map((row) => row.l2),
  );
  $: typeOptions = uniqueValues(processes.map((row) => row.type));
  $: teamOptions = uniqueValues(processes.flatMap((row) => row.teams || []));
  $: filteredRows = sortRows(
    processes.filter((row) => {
      const query = search.trim().toLowerCase();
      return (
        (!query ||
          [row.l1, row.l2, row.l3, row.l4].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(query),
          )) &&
        (!selectedL1 || row.l1 === selectedL1) &&
        (!selectedL2 || row.l2 === selectedL2) &&
        (!selectedType || row.type === selectedType) &&
        (!selectedTeam || row.teams?.includes(selectedTeam))
      );
    }),
  );

  onMount(async () => {
    selectedL1 = new URLSearchParams(window.location.search).get("l1") || "";
    await Promise.all([loadProjects(), loadCatalog()]);
  });

  async function loadProjects() {
    try {
      const response = await fetchWithSession("/api/monitoring/projects");
      if (response.ok) projects = await response.json();
    } catch (loadError) {
      console.warn("Monitoring projects are unavailable:", loadError);
    }
  }

  async function loadCatalog() {
    loading = true;
    error = "";
    page = 1;
    try {
      const query = selectedProject
        ? `?project_id=${encodeURIComponent(selectedProject)}`
        : "";
      const response = await fetchWithSession(
        `/api/monitoring/processes/catalog${query}`,
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.message || "Process Catalog could not be loaded.",
        );
      }
      processes = result.processes || [];
      summary = result.summary || {};
    } catch (loadError) {
      error = loadError.message || "Process Catalog could not be loaded.";
    } finally {
      loading = false;
    }
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
  }

  function resetPage() {
    page = 1;
  }

  function sortRows(rows) {
    const modifier = sortDirection === "asc" ? 1 : -1;
    return [...rows].sort((left, right) => {
      if (numericKeys.has(sortKey)) {
        return (
          (Number(left[sortKey] || 0) - Number(right[sortKey] || 0)) * modifier
        );
      }
      return (
        String(left[sortKey] || "").localeCompare(
          String(right[sortKey] || ""),
        ) * modifier
      );
    });
  }

  function formatDuration(seconds) {
    const value = Number(seconds || 0);
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k h`;
    if (hours) return `${hours}:${String(minutes).padStart(2, "0")} h`;
    return `${minutes}:${String(Math.round(value % 60)).padStart(2, "0")} min`;
  }

  function formatAnnualTime(seconds) {
    const value = Number(seconds || 0);
    if (value >= 3600) return `${Math.ceil(value / 3600)} h`;
    return `${Math.ceil(value / 60)} min`;
  }

  function formatCompact(value) {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(Number(value || 0));
  }

  function formatCost(value) {
    return `${formatCompact(value)} €`;
  }

  function formatCell(row, key) {
    if (numericKeys.has(key) && row.has_kpis === false) return "N/A";
    if (key === "time_pa") return formatAnnualTime(row[key]);
    if (["avg_time", "median_time"].includes(key))
      return formatDuration(row[key]);
    if (key === "cost_pa") return formatCost(row[key]);
    if (key === "frequency_pa") return formatCompact(row[key]);
    if (key === "teams") return (row.teams || []).join(", ") || "N/A";
    if (key === "projects") return (row.projects || []).join(", ") || "N/A";
    return row[key] ?? "N/A";
  }

  function headerLabel(key, label) {
    const count = summary[`${key}_count`];
    return count == null ? label : `${label} (${count})`;
  }

  function typeLabel(value) {
    return String(value || "core").replace(/^./, (letter) =>
      letter.toUpperCase(),
    );
  }

  function openActions(row) {
    selectedActionRow = row;
    actionsOpen = true;
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Process Intelligence"
  areaClickable={false}
  breadcrumb="Process Catalog"
  tooltipText="Process Catalog combines transferred L1–L4 hierarchies and calculated task KPIs across projects. Filter the portfolio by process type, hierarchy, team or source project."
/>

<SectionWrapper
  fullscreen
  fitContent
  wrapperClass="process-catalog-wrapper"
  canvasClass="process-catalog-canvas"
>
  <div class="catalog-main" slot="main">
    <div class="filter-bar">
      <div class="filter-field search-field">
        <SearchNew
          placeholder="Process or task name"
          bind:searchTerm={search}
          flexGrow
        />
      </div>
      <div class="filter-field">
        <Select
          placeholder="All L1 processes"
          items={l1Options.map((value) => ({ text: value, value }))}
          bind:selected={selectedL1}
          onchange={() => {
            selectedL2 = "";
            resetPage();
          }}
          color="dark"
          labelText="L1 Process"
        />
      </div>
      <div class="filter-field">
        <Select
          placeholder="All L2 subprocesses"
          items={l2Options.map((value) => ({ text: value, value }))}
          bind:selected={selectedL2}
          onchange={resetPage}
          color="dark"
          labelText="L2 Subprocess"
        />
      </div>
      <div class="filter-field">
        <Select
          placeholder="All types"
          items={typeOptions.map((value) => ({
            text: typeLabel(value),
            value,
          }))}
          bind:selected={selectedType}
          onchange={resetPage}
          color="dark"
          labelText="Type"
        />
      </div>
      <div class="filter-field">
        <Select
          placeholder="All teams"
          items={teamOptions.map((value) => ({ text: value, value }))}
          bind:selected={selectedTeam}
          onchange={resetPage}
          color="dark"
          labelText="Team"
        />
      </div>
      <div class="filter-field">
        <Select
          placeholder="All projects"
          items={projects.map((project) => ({
            text: project.name,
            value: project.id,
          }))}
          bind:selected={selectedProject}
          onchange={loadCatalog}
          color="dark"
          labelText="Project"
        />
      </div>
    </div>

    <section class="catalog-table">
      {#if loading}
        <DataTableSkeleton
          rows={10}
          {headers}
          showToolbar={false}
          showHeader={false}
          size="compact"
        />
      {:else if error}
        <div class="empty-state error">{error}</div>
      {:else if !filteredRows.length}
        <div class="empty-state">
          No transferred process results match the selected filters.
        </div>
      {:else}
        <SoftwareCatalogTable
          headers={headers.map((header) => ({
            ...header,
            value: headerLabel(header.key, header.value),
          }))}
          rows={filteredRows}
          variant="process"
          bind:page
          bind:pageSize
          pageSizes={[10, 25, 50]}
          sortable={true}
          minWidth="100rem"
          formatProcessCell={formatCell}
          onProcessAction={openActions}
        />
      {/if}
    </section>
  </div>
</SectionWrapper>

<Modal
  bind:open={actionsOpen}
  modalHeading="Actions"
  subtitle={selectedActionRow?.l4 || ""}
  hideFooter
  size="sm"
>
  <div class="actions-placeholder">placeholder</div>
</Modal>

<style>
  :global(.process-catalog-wrapper .view_wrapper),
  :global(.process-catalog-wrapper .process-catalog-canvas) {
    background: var(--pax-smoke);
  }

  :global(.process-catalog-wrapper .process-catalog-canvas) {
    width: 100%;
    padding: 1rem;
  }

  .catalog-main {
    display: flex;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    gap: 1rem;
  }

  .filter-bar {
    display: grid;
    grid-template-columns: minmax(15rem, 1.35fr) repeat(5, minmax(9rem, 1fr));
    gap: 0.65rem;
    align-items: end;
    padding: 0.65rem;
    border-radius: 0.5rem;
    background: #fff;
  }

  .filter-field {
    display: flex;
    min-width: 0;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0.2rem;
  }
  .search-field :global(.search) {
    height: 2.5rem;
    padding: 0 0.75rem;
    border-radius: 0.2rem;
  }
  .search-field :global(.searchicon) {
    height: 1.25rem;
  }
  .search-field :global(input) {
    height: 2.25rem;
    padding-left: 0.75rem;
    box-shadow: none !important;
  }
  .search-field :global(input:focus) {
    box-shadow: none !important;
  }
  .filter-field :global(.singleSelect_wrapper),
  .filter-field :global(.button_wrapper) {
    width: 100%;
    min-width: 0;
  }

  .catalog-table {
    min-width: 0;
    overflow: hidden;
    border-radius: 0.5rem;
    background: #fff;
  }
  .actions-placeholder {
    min-height: 6rem;
    padding: 0.5rem 0;
    color: #4c5560;
    font-size: 0.8rem;
  }

  .empty-state {
    display: grid;
    min-height: 18rem;
    place-items: center;
    color: #6c7580;
    font-size: 0.75rem;
  }
  .empty-state.error {
    color: #da1e28;
  }

  @media (max-width: 90rem) {
    .filter-bar {
      grid-template-columns: repeat(3, minmax(10rem, 1fr));
    }
  }
</style>
